import React, { useState } from 'react';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import { useCache } from '../hooks/useCache';

interface RetentionConfig {
  auditTrailRetentionDays: number;
  userActivityRetentionDays: number;
  autoCleanupEnabled: boolean;
}

const RetentionSettings: React.FC = () => {
  const [saving, setSaving] = useState(false);

  // Use cache for retention settings
  const { 
    data: retentionConfig, 
    loading, 
    refresh: refreshConfig 
  } = useCache(
    'admin-retention-settings',
    async () => {
      try {
        const response = await apiCall('/admin/retention-settings');
        return response;
      } catch (error) {
        // Return default values if not configured yet
        return {
          auditTrailRetentionDays: 365,
          userActivityRetentionDays: 90,
          autoCleanupEnabled: true
        };
      }
    },
    { ttl: 5 * 60 * 1000 } // 5 minutes cache
  );

  const [settings, setSettings] = useState<RetentionConfig>({
    auditTrailRetentionDays: retentionConfig?.auditTrailRetentionDays || 365,
    userActivityRetentionDays: retentionConfig?.userActivityRetentionDays || 90,
    autoCleanupEnabled: retentionConfig?.autoCleanupEnabled ?? true
  });

  // Update local state when cache data changes
  React.useEffect(() => {
    if (retentionConfig) {
      setSettings({
        auditTrailRetentionDays: retentionConfig.auditTrailRetentionDays || 365,
        userActivityRetentionDays: retentionConfig.userActivityRetentionDays || 90,
        autoCleanupEnabled: retentionConfig.autoCleanupEnabled ?? true
      });
    }
  }, [retentionConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiCall('/admin/retention-settings', {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
      
      toast.success('Retention settings updated successfully');
      refreshConfig();
    } catch (error) {
      console.error('Error updating retention settings:', error);
      toast.error('Failed to update retention settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      auditTrailRetentionDays: 365,
      userActivityRetentionDays: 90,
      autoCleanupEnabled: true
    });
  };

  const calculateStorageImpact = () => {
    const auditImpact = settings.auditTrailRetentionDays / 365;
    const activityImpact = settings.userActivityRetentionDays / 90;
    return {
      auditImpact: auditImpact.toFixed(1),
      activityImpact: activityImpact.toFixed(1)
    };
  };

  const storageImpact = calculateStorageImpact();

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Data Retention Settings</h3>
        <p className="text-sm text-gray-600">
          Configure how long audit trails and user activity logs are stored. Longer retention periods require more storage space.
        </p>
      </div>

      <div className="space-y-6">
        {/* Audit Trail Retention */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Admin Audit Trail Retention
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="number"
              min="30"
              max="2555" // ~7 years
              value={settings.auditTrailRetentionDays}
              onChange={(e) => setSettings({
                ...settings,
                auditTrailRetentionDays: Math.max(30, Number(e.target.value))
              })}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-sm text-gray-600">days</span>
            <span className="text-xs text-gray-500">
              ({Math.round(settings.auditTrailRetentionDays / 30)} months, Storage impact: {storageImpact.auditImpact}x)
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Minimum: 30 days. Recommended: 365 days for compliance.
          </p>
        </div>

        {/* User Activity Retention */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            User Activity Logs Retention
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="number"
              min="7"
              max="365"
              value={settings.userActivityRetentionDays}
              onChange={(e) => setSettings({
                ...settings,
                userActivityRetentionDays: Math.max(7, Number(e.target.value))
              })}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-sm text-gray-600">days</span>
            <span className="text-xs text-gray-500">
              ({Math.round(settings.userActivityRetentionDays / 30)} months, Storage impact: {storageImpact.activityImpact}x)
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Minimum: 7 days. Recommended: 90 days for monitoring.
          </p>
        </div>

        {/* Auto Cleanup */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.autoCleanupEnabled}
              onChange={(e) => setSettings({
                ...settings,
                autoCleanupEnabled: e.target.checked
              })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">
              Enable automatic cleanup
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Automatically delete logs older than the retention period. Runs daily at midnight UTC.
          </p>
        </div>

        {/* Storage Impact Warning */}
        {(settings.auditTrailRetentionDays > 365 || settings.userActivityRetentionDays > 90) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800">
                  Increased Storage Usage
                </h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Extended retention periods will increase storage costs. Monitor your database size regularly.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200 mt-6">
        <button
          onClick={handleReset}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Reset to Defaults
        </button>
        
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default RetentionSettings;